/**
 * FO-036 (batch-16) — Document-register: types, DTO's en Zod-schemas.
 *
 * Het register zit bovenop `ClientDocumentTemplate`/`DocumentVersion`:
 *  - `DocumentTypeCode` = de beheerbare legenda (type-code -> label).
 *  - `DocumentRegistratie` = één beheerst document (registratienummer = code +
 *    versie) met status + twee-traps goedkeuring (HR + management).
 *  - Het exemplaar-volgnummer (code + jaar + nr) wordt gat-vrij getrokken bij het
 *    genereren (zie apps/api/src/lib/document-register-numbers.ts).
 *
 * Gedeeld schema = de API (`.parse`/`.safeParse`) én het form valideren ermee.
 */

import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { idParam } from './id-validators'
// Hergebruik de bestaande categorie-enum/labels uit document-templates (geen
// duplicaat — die zou in de package-index colliden).
import { DOCUMENT_TEMPLATE_CATEGORIES, type DocumentTemplateCategory } from './document-templates'

// ─── Enums (matching Prisma) ──────────────────────────────────────────────────

export const DOCUMENT_REGISTRATIE_STATUS = [
  'CONCEPT',
  'TER_GOEDKEURING',
  'GEREGISTREERD',
  'VERVALLEN',
] as const
export type DocumentRegistratieStatus = (typeof DOCUMENT_REGISTRATIE_STATUS)[number]

export const DOCUMENT_REGISTRATIE_STATUS_LABELS: Record<DocumentRegistratieStatus, string> = {
  CONCEPT:         'Concept',
  TER_GOEDKEURING: 'Ter goedkeuring',
  GEREGISTREERD:   'Geregistreerd',
  VERVALLEN:       'Vervallen',
}

/** Audit-acties op een registratie (string-kolom; union voor NL-labels). */
export const DOCUMENT_REGISTRATIE_AUDIT_ACTIES = [
  'AANGEMAAKT',
  'INGEDIEND',
  'HR_GOEDGEKEURD',
  'MANAGEMENT_GOEDGEKEURD',
  'GEREGISTREERD',
  'NIEUWE_VERSIE',
  'VERVALLEN',
  'AFGEKEURD',
  'TERUGGETROKKEN',
] as const
export type DocumentRegistratieAuditActie = (typeof DOCUMENT_REGISTRATIE_AUDIT_ACTIES)[number]

export const DOCUMENT_REGISTRATIE_AUDIT_ACTIE_LABELS: Record<string, string> = {
  AANGEMAAKT:             'Concept aangemaakt',
  INGEDIEND:              'Ingediend ter goedkeuring',
  HR_GOEDGEKEURD:         'HR goedgekeurd',
  MANAGEMENT_GOEDGEKEURD: 'Management goedgekeurd',
  GEREGISTREERD:          'Geregistreerd',
  NIEUWE_VERSIE:          'Nieuwe versie gemaakt',
  VERVALLEN:              'Vervallen',
  AFGEKEURD:              'Afgekeurd',
  TERUGGETROKKEN:         'Teruggetrokken',
}

// Lokale (niet-geëxporteerde) tuple voor de Zod-enum; type/labels komen uit
// document-templates (zie import boven). FO-400 (batch-49): AFGELEID van
// DOCUMENT_TEMPLATE_CATEGORIES i.p.v. een handmatige kopie - die liep uit de pas
// (AANZEGBRIEF/GETUIGSCHRIFT/EINDE_DIENSTVERBAND_BRIEF ontbraken), waardoor het
// register een geldige categorie kon weigeren. Nu kan dat niet meer.
const TEMPLATE_CATEGORY_VALUES = DOCUMENT_TEMPLATE_CATEGORIES as unknown as [
  DocumentTemplateCategory,
  ...DocumentTemplateCategory[],
]

// ─── Zod: legenda (DocumentTypeCode) ──────────────────────────────────────────

/** Code = hoofdletters/cijfers + koppeltekens (AOK-BT, GVS); auto-uppercased. */
const typeCodeString = z
  .string()
  .trim()
  .min(2)
  .max(20)
  .transform((v) => v.toUpperCase())
  .refine((v) => /^[A-Z0-9][A-Z0-9-]*[A-Z0-9]$/.test(v), {
    message: 'Code: hoofdletters/cijfers en koppeltekens (bijv. AOK-BT)',
  })

export const createDocumentTypeCodeSchema = z.object({
  code: typeCodeString,
  label: z.string().trim().min(1).max(120),
  omschrijving: z.string().trim().max(500).nullable().optional(),
  categorie: z.enum(TEMPLATE_CATEGORY_VALUES).nullable().optional(),
  actief: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
})
export type CreateDocumentTypeCodeInput = z.infer<typeof createDocumentTypeCodeSchema>

export const updateDocumentTypeCodeSchema = z.object({
  label: z.string().trim().min(1).max(120).optional(),
  omschrijving: z.string().trim().max(500).nullable().optional(),
  categorie: z.enum(TEMPLATE_CATEGORY_VALUES).nullable().optional(),
  actief: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
})
export type UpdateDocumentTypeCodeInput = z.infer<typeof updateDocumentTypeCodeSchema>

// ─── Zod: registratie (beheerst document) ─────────────────────────────────────

/** Een nieuw beheerst document (v1, status CONCEPT). */
export const createDocumentRegistratieSchema = z.object({
  typeCodeId: idParam('typeCodeId'),
  naam: z.string().trim().min(1).max(200),
  /** Optioneel: het bruikbare DOCX-sjabloon dat dit beheerste document gebruikt. */
  templateId: idParam('templateId').nullable().optional(),
  /** Optionele periodieke review-/herregistratie-datum (yyyy-MM-dd). */
  reviewDatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
})
export type CreateDocumentRegistratieInput = z.infer<typeof createDocumentRegistratieSchema>

/** Een concept-registratie bewerken (alleen toegestaan in CONCEPT). */
export const updateDocumentRegistratieSchema = z.object({
  naam: z.string().trim().min(1).max(200).optional(),
  templateId: idParam('templateId').nullable().optional(),
  reviewDatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
})
export type UpdateDocumentRegistratieInput = z.infer<typeof updateDocumentRegistratieSchema>

/** Optionele toelichting bij goedkeuren/afkeuren. */
export const documentRegistratieBesluitSchema = z.object({
  toelichting: z.string().trim().max(1000).optional(),
})
export type DocumentRegistratieBesluitInput = z.infer<typeof documentRegistratieBesluitSchema>

export const listDocumentRegisterQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
  typeCodeId: idParam('typeCodeId').optional(),
  status: z.enum(DOCUMENT_REGISTRATIE_STATUS).optional(),
  categorie: z.enum(TEMPLATE_CATEGORY_VALUES).optional(),
  /** Alleen de laatste versie per type-code tonen (default true). */
  alleenLaatste: queryBoolean().optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(20),
})
export type ListDocumentRegisterQuery = z.infer<typeof listDocumentRegisterQuerySchema>

// ─── DTO's ────────────────────────────────────────────────────────────────────

export interface DocumentTypeCodeDTO {
  id: string
  code: string
  label: string
  omschrijving: string | null
  categorie: DocumentTemplateCategory | null
  actief: boolean
  sortOrder: number
  /** Aantal registraties met deze code (alle versies). */
  aantalRegistraties: number
}

export interface DocumentRegistratieAuditDTO {
  id: string
  actie: string
  actor: string
  actorName: string | null
  vanStatus: DocumentRegistratieStatus | null
  naarStatus: DocumentRegistratieStatus | null
  toelichting: string | null
  createdAt: string
}

export interface DocumentRegistratieListRowDTO {
  id: string
  code: string
  /** registratienummer = code + " v" + versie, bijv. "AOK-BT v2". */
  registratienummer: string
  versie: number
  naam: string
  status: DocumentRegistratieStatus
  typeLabel: string
  categorie: DocumentTemplateCategory | null
  /** True als dit de laatste (hoogste) versie van de type-code is. */
  isLaatste: boolean
  /** True als bruikbaar in de docgen (GEREGISTREERD én laatste versie). */
  bruikbaar: boolean
  heeftSjabloon: boolean
  heeftVoorbeeld: boolean
  eigenaarNaam: string | null
  hrGoedgekeurdOp: string | null
  managementGoedgekeurdOp: string | null
  geregistreerdOp: string | null
  reviewDatum: string | null
  updatedAt: string
}

export interface DocumentRegistratieDetailDTO {
  id: string
  typeCodeId: string
  code: string
  registratienummer: string
  versie: number
  naam: string
  status: DocumentRegistratieStatus
  typeLabel: string
  categorie: DocumentTemplateCategory | null
  isLaatste: boolean
  bruikbaar: boolean
  templateId: string | null
  templateNaam: string | null
  voorbeeldDocumentId: string | null
  voorbeeldFileName: string | null
  eigenaar: string
  eigenaarNaam: string | null
  hrGoedgekeurdDoor: string | null
  hrGoedgekeurdDoorNaam: string | null
  hrGoedgekeurdOp: string | null
  managementGoedgekeurdDoor: string | null
  managementGoedgekeurdDoorNaam: string | null
  managementGoedgekeurdOp: string | null
  ingediendOp: string | null
  geregistreerdOp: string | null
  vervallenOp: string | null
  vervangtId: string | null
  reviewDatum: string | null
  createdAt: string
  updatedAt: string
  audits: DocumentRegistratieAuditDTO[]
}
